import { EventEmitter, Injectable } from '@angular/core';
import { interval, Observable } from 'rxjs';
import { distinctUntilChanged, shareReplay } from 'rxjs/operators';

// details: https://developer.mozilla.org/en-US/docs/Web/API/Page_Visibility_API

declare var document: Document & {
  msHidden?: boolean;
  webkitHidden?: boolean;
};

export let hidden: 'hidden' | 'msHidden' | 'webkitHidden';
export let visibilityChange:
  | 'visibilitychange'
  | 'msvisibilitychange'
  | 'webkitvisibilitychange';

if (typeof document.hidden !== 'undefined') {
  // Opera 12.10 and Firefox 18 and later support
  hidden = 'hidden';
  visibilityChange = 'visibilitychange';
} else if (typeof document.msHidden !== 'undefined') {
  hidden = 'msHidden';
  visibilityChange = 'msvisibilitychange';
} else if (typeof document.webkitHidden !== 'undefined') {
  hidden = 'webkitHidden';
  visibilityChange = 'webkitvisibilitychange';
}

const MANUAL_VISIBILITY_CHECK_INTERVAL = 1000;

@Injectable()
export class DocumentVisibilityService {
  public get documentVisible$(): Observable<boolean> {
    return this.documentVisibleEmitter.pipe(
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  public get documentVisible() {
    return !document[hidden];
  }

  private documentVisibleEmitter = new EventEmitter<boolean>();

  constructor() {
    document.addEventListener(
      visibilityChange,
      this.handleVisibilityChange.bind(this),
    );
    this.handleVisibilityChange();

    // Required to handle slow application bootstrap
    interval(MANUAL_VISIBILITY_CHECK_INTERVAL).subscribe(
      this.handleVisibilityChange.bind(this),
    );
  }

  private handleVisibilityChange() {
    this.documentVisibleEmitter.next(this.documentVisible);
  }
}
